import DiamondIcon from "@mui/icons-material/Diamond";
import MonitorIcon from "@mui/icons-material/Monitor";
import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { useContext, useEffect } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import CustomSwitch from "../../../../components/CustomSwitch";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { ProtectionLevelText } from "../../../../utils/constants";

import { useLocation } from "react-router-dom";
import {
  icons,
  severities,
  sidebarIcons,
} from "../../../stack/environment/icons/iconExport";
import DetectionLabelIcon from "../../detections/components/DetectionLabelIcon";

const ScenarioSetLegendSidebar = ({
  isMitigatedShown,
  setIsMitigatedShown,
  isMitigatedDisabled,
  isMitigatedDisabledTooltip,
  isFocusedScenarioShown,
  setIsFocusedScenarioShown,
  isFocusedScenarioDisabled,
  isFocusedScenarioDisabledTooltip,
  isActiveDetectionsShown,
  setIsActiveDetectionsShown,
  isActiveDetectionsDisabled,
  isActiveDetectionsDisabledTooltip,
}) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const location = useLocation();

  const handleToggleNonMitigatedScenarios = () => {
    trackEvent(
      `User toggled show only non-mitigated scenarios switch ${
        isMitigatedShown ? "on" : "off"
      }`
    );
    setIsMitigatedShown(!isMitigatedShown);
  };

  const handleToggleFocusedScenarios = () => {
    trackEvent(
      `User toggled show only entity-based scenarios switch ${
        isFocusedScenarioShown ? "off" : "on"
      }`
    );
    setIsFocusedScenarioShown(!isFocusedScenarioShown);
  };

  const handleToggleActiveDetections = () => {
    trackEvent(
      `User toggled show only active detections switch ${
        isActiveDetectionsShown ? "off" : "on"
      }`
    );
    setIsActiveDetectionsShown(!isActiveDetectionsShown);
  };
  useEffect(() => {
    const localSearchParams = new URLSearchParams(location.search);
    const showActiveDetection = decodeURIComponent(
      localSearchParams.get("showActiveDetection")
    );
    if (showActiveDetection === "true") {
      setIsActiveDetectionsShown(true);
    }
  }, [location.search]);

  const renderNonMitigatedSwitch = () => (
    <FormControlLabel
      sx={{
        justifyContent: "space-between",
        alignItems: "flex-start",
        mt: "16px",
        display: "flex",
        ml: "0",
      }}
      control={
        <CustomSwitch
          checked={!isMitigatedShown}
          onChange={handleToggleNonMitigatedScenarios}
          disabled={isMitigatedDisabled}
          tooltipTitle={isMitigatedDisabledTooltip}
        />
      }
      label={
        <Typography
          variant="caption"
          sx={{
            ml: "8px",
            display: "inline",
            alignItems: "center",
            wordWrap: "break-word",
          }}
        >
          Show only high priority
        </Typography>
      }
    />
  );

  const renderFocusedScenariosSwitch = () => (
    <FormControlLabel
      control={
        <CustomSwitch
          checked={isFocusedScenarioShown}
          onChange={handleToggleFocusedScenarios}
          disabled={isFocusedScenarioDisabled}
          tooltipTitle={isFocusedScenarioDisabledTooltip}
        />
      }
      label={
        <Typography
          variant="caption"
          sx={{
            ml: "8px",
            display: "inline",
            alignItems: "center",
            wordWrap: "break-word",
          }}
        >
          Show only entity-based
        </Typography>
      }
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        mt: "16px",
        display: "flex",
        ml: "0",
      }}
    />
  );

  const renderActiveDetectionsSwitch = () => (
    <FormControlLabel
      control={
        <CustomSwitch
          checked={isActiveDetectionsShown}
          onChange={handleToggleActiveDetections}
          disabled={isActiveDetectionsDisabled}
          tooltipTitle={isActiveDetectionsDisabledTooltip}
        />
      }
      label={
        <Typography
          variant="caption"
          sx={{
            ml: "8px",
            display: "inline",
            alignItems: "center",
            wordWrap: "break-word",
          }}
        >
          Show only active detections
        </Typography>
      }
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        mt: "16px",
        display: "flex",
        ml: "0",
      }}
    />
  );

  const protectionLevels = [
    {
      color: colors.error_main,
      text: ProtectionLevelText.AT_RISK,
    },
    {
      color: colors.warning_dark,
      text: ProtectionLevelText.DETECTION_ONLY,
    },
    {
      color: colors.tracking_on_hold,
      text: ProtectionLevelText.PARTIAL_PROTECTION,
    },
    {
      color: colors.success_main,
      text: ProtectionLevelText.OPTIMAL_PROTECTION,
    },
    {
      color: colors.grey_600,
      text: ProtectionLevelText.RISK_ACCEPTED,
    },
  ];

  const assetComponent = () => {
    const containerStyle = {
      position: "relative",
      overflow: "visible",
      width: "32px",
      height: "32px",
      zIndex: 0,
    };

    return (
      <Grid
        item
        xs={12}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          display: "flex",
        }}
      >
        <Box sx={containerStyle}>
          <SvgToIcon
            IconLogo={icons.CriticalAssetHexagon}
            width="100%"
            height="100%"
          />

          <SvgToIcon
            IconLogo={DiamondIcon}
            width={18}
            style={{
              color: colors.primary_main,
              position: "absolute",
              top: 9,
              left: 7,
            }}
          />
        </Box>
        <Typography variant="caption">Critical asset</Typography>
      </Grid>
    );
  };

  const locationComponent = () => {
    const containerStyle = {
      position: "relative",
      overflow: "visible",
      width: "32px",
      height: "32px",
      zIndex: 0,
    };

    return (
      <Grid
        item
        xs={12}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          display: "flex",
        }}
      >
        <Box sx={containerStyle}>
          <SvgToIcon
            IconLogo={icons.LocationHexagon}
            width="100%"
            height="100%"
          />

          <SvgToIcon
            IconLogo={MonitorIcon}
            width={18}
            style={{
              color: colors.common_black,
              position: "absolute",
              top: 7,
              left: 7,
            }}
          />
        </Box>
        <Typography variant="caption">Endpoint</Typography>
      </Grid>
    );
  };

  const titleComponent = (title, color = "primary") => {
    return (
      <Typography
        variant="overline"
        sx={{
          color:
            color === "primary" ? colors.text_primary : colors.text_secondary,
        }}
      >
        {title}
      </Typography>
    );
  };

  const protectionLevelComponent = (level, index) => {
    return (
      <Grid
        key={index}
        item
        xs={12}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mt: "16px",
          display: "flex",
        }}
      >
        <Box
          sx={{
            width: "18px",
            height: "18px",
            minWidth: "18px",
            background: level.color,
            borderRadius: "50%",
          }}
        />

        <Typography variant="caption">{level.text}</Typography>
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        padding: "16px",
        backgroundColor: colors.grey_100,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${colors.scenario_graph_border}`,
        borderRadius: "10px",
      }}
      style={{ overflow: "auto" }}
    >
      <Grid
        container
        direction="row"
        sx={{
          display: "flex",
          gap: 3,
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            display: "flex",
          }}
        >
          {titleComponent("View", "secondary")}
          {renderNonMitigatedSwitch()}
          {renderFocusedScenariosSwitch()}
          {detectionsActive && renderActiveDetectionsSwitch()}
        </Grid>
        <Divider
          sx={{
            backgroundColor: colors.divider,
            height: 2,
            width: "100%",
          }}
        />

        {/* Coverage */}
        <Grid
          item
          xs={12}
          sx={{
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            display: "flex",
          }}
        >
          {titleComponent("Protection Levels")}

          {protectionLevels.map((level, index) =>
            protectionLevelComponent(level, index)
          )}
        </Grid>

        {/*Location*/}
        <Grid
          item
          xs={12}
          sx={{
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            display: "flex",
            gap: 2,
          }}
        >
          {titleComponent("Location")}
          {assetComponent()}
          {locationComponent()}
        </Grid>
        {/*Impact*/}
        <Grid
          item
          xs={12}
          sx={{
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            display: "flex",
          }}
        >
          {titleComponent("Impact")}
          {/*Detection*/}
          {detectionsActive && (
            <Grid
              item
              xs={12}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mt: "12px",
                display: "flex",
              }}
            >
              <Box
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                <DetectionLabelIcon />
              </Box>

              <Typography variant="caption">Active Detection</Typography>
            </Grid>
          )}
          {/* Vulnerability */}
          <Grid
            item
            xs={12}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mt: "12px",
              display: "flex",
            }}
          >
            <Box
              sx={{
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
              }}
            >
              <SvgToIcon
                IconLogo={sidebarIcons.VulnerabilitiesIcon}
                width={24}
                style={{
                  color: colors.error_dark,
                }}
              />
            </Box>

            <Typography variant="caption">Vulnerability</Typography>
          </Grid>
          {/* Exploit in the wild */}
          <Grid
            item
            xs={12}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mt: "12px",
              display: "flex",
            }}
          >
            <Box
              sx={{
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
              }}
            >
              <SvgToIcon IconLogo={icons.exploitInTheWildIcon} width={24} />
            </Box>

            <Typography variant="caption">Exploit in the wild</Typography>
          </Grid>{" "}
          {/* High Impact Step */}
          <Grid
            item
            xs={12}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mt: "12px",
              display: "flex",
            }}
          >
            <Box
              sx={{
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
              }}
            >
              <SvgToIcon IconLogo={severities.SeverityBlackIcon} width={24} />
            </Box>

            <Typography variant="caption">High-Impact Step</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ScenarioSetLegendSidebar;
